import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, FileQuestion } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Was der Phase an Dokumenten fehlt — über dem Datenraum, nicht darin.
 *
 * Der Baum zeigt eine Ablage: was da ist, wo es liegt, in welchem Zustand. Ein fehlendes Dokument
 * ist nichts davon. Es hat keinen Pfad, keinen Ordner und keine Datei, und als Zeile zwischen den
 * anderen nimmt es genau die Eigenschaften an, die es nicht hat — anklickbar aussehend, in einer
 * Sortierung stehend, in einen Ordner gehörend.
 *
 * Deshalb ein eigener Bereich darüber. Er beantwortet die umgekehrte Frage des Baums: nicht „was
 * liegt hier", sondern „was fehlt noch". Wer sie sonst beantworten wollte, müsste die
 * Anforderungsliste danebenlegen und abhaken.
 */

export interface MissingDocument {
    role: string;
    subtype?: string;
    label: string;
}

interface Properties {
    documents: MissingDocument[];
    /** Enger gesetzt, wo der Datenraum in eine Spalte eingebettet ist. */
    dense?: boolean;
}

export default function MissingDocuments({ documents, dense }: Properties) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(true);

    // Nichts zu melden ist keine Meldung. Ein leerer Bereich mit einer Null darin nähme dem Baum
    // Platz weg, um zu sagen, dass es nichts zu sagen gibt.
    if (documents.length === 0) return <></>;

    const Chevron = open ? ChevronDown : ChevronRight;

    return (
        <div className="flex min-w-0 shrink-0 flex-col overflow-hidden rounded-lg border border-amber-500/40 bg-amber-500/5">
            {/*
             * Zusammenklappbar, weil die Zahl nicht in unserer Hand liegt: Eine Phase kann zwei
             * Dokumente vermissen oder zwanzig, und zwanzig Zeilen über dem Baum ließen von ihm
             * nichts übrig.
             */}
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
                className={cn(
                    'flex min-w-0 items-center gap-2 text-left transition-colors hover:bg-amber-500/10',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    dense ? 'px-2 py-1.5' : 'px-3 py-2',
                )}
            >
                <Chevron className="h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-500" />
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-amber-800 dark:text-amber-400">
                    {t('portfolioDashboard.dataRoom.missingTitle')}
                </span>
                <span className="shrink-0 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-semibold tabular-nums text-amber-800 dark:text-amber-400">
                    {documents.length}
                </span>
            </button>

            {open ? (
                // Eigener Bildlauf mit Deckel: Der Bereich darf wachsen, aber nicht den Baum
                // verdrängen, für den die Ansicht da ist.
                <div className={cn('max-h-40 overflow-y-auto', dense ? 'px-2 pb-1.5' : 'px-3 pb-2')}>
                    <ul className="flex flex-col gap-0.5">
                        {documents.map((document) => (
                            <li
                                key={`${document.role}:${document.subtype ?? ''}`}
                                className="flex min-w-0 items-baseline gap-2 py-0.5"
                            >
                                <FileQuestion className="h-3.5 w-3.5 shrink-0 translate-y-0.5 text-amber-700/70 dark:text-amber-500/70" />
                                <span className="min-w-0 flex-1 truncate text-xs" title={document.label}>
                                    {document.label}
                                </span>
                                {/* Die Rolle dahinter, weil die Bezeichnung aus der Vorlage stammt
                                    und nicht immer sagt, wonach zu suchen ist. */}
                                <span
                                    className="shrink-0 truncate text-[11px] text-muted-foreground"
                                    title={document.subtype ? `${document.role} › ${document.subtype}` : document.role}
                                >
                                    {document.subtype ? `${document.role} › ${document.subtype}` : document.role}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <></>
            )}
        </div>
    );
}
